export const AI_DASHBOARD_ADDON_DEFINITIONS = [
  {
    key: 'ai_assistant',
    label: 'AI Assistant',
    description: 'Multi-provider conversational AI assistant — branded chat, multiple agents, usage analytics and licensing. Purchasable as a paid add-on for any subscription or tenant.',
    tabId: 'ai-assistant-chat',
    defaultPricing: {
      monthly_price: 149,
      yearly_price: 1490,
    },
  },
  {
    key: 'ai_attendance_insight',
    label: 'AI Attendance Insight',
    description: 'Attendance scoring, attendance-risk visibility, and workforce activity signals.',
    tabId: 'ai-center/attendance-insight',
    defaultPricing: {
      monthly_price: 1499,
      yearly_price: 14999,
    },
  },
  {
    key: 'ai_hiring_forecast',
    label: 'AI Hiring Forecast',
    description: 'Hiring demand modeling, candidate quality signals, and funnel bottleneck forecasting.',
    tabId: 'ai-center/hiring-forecast',
    defaultPricing: {
      monthly_price: 1999,
      yearly_price: 19999,
    },
  },
  {
    key: 'ai_payroll_forecast',
    label: 'AI Payroll Forecast',
    description: 'Payroll cost forecasting, anomaly visibility, and compensation pressure analysis.',
    tabId: 'ai-center/payroll-forecast',
    defaultPricing: {
      monthly_price: 2499,
      yearly_price: 24999,
    },
  },
  {
    key: 'ai_team_performance',
    label: 'AI Team Performance',
    description: 'Team productivity, burnout risk, engagement trends, and skill-gap visibility.',
    tabId: 'ai-center/team-performance',
    defaultPricing: {
      monthly_price: 1799,
      yearly_price: 17999,
    },
  },
  {
    key: 'ai_settings',
    label: 'AI Settings',
    description: 'AI model controls, training configuration, permissions, and capability governance.',
    tabId: 'ai-center/settings',
    defaultPricing: {
      monthly_price: 999,
      yearly_price: 9999,
    },
  },
];

// Education Suite modules, sold à la carte. Each key is BOTH the paid add-on key and
// the module gate — enabling one grants exactly that module (the backend adds the
// matching edu_* module_access key). No blanket "unlock everything" toggle.
export const SUITE_ADDON_DEFINITIONS = [
  {
    key: 'edu_faculty',
    label: 'Education · Faculty Management',
    description: 'Faculty profiles that extend employees — academic rank, faculty type, specialization, workload, committees and mentor groups.',
    tabId: 'edu-faculty',
    defaultPricing: { monthly_price: 1499, yearly_price: 14999 },
  },
  {
    key: 'edu_calendar',
    label: 'Education · Academic Calendar',
    description: 'Academic years, semesters/terms, and academic events (exams, holidays, admissions) with teaching-blocked dates.',
    tabId: 'edu-calendar',
    defaultPricing: { monthly_price: 799, yearly_price: 7999 },
  },
  {
    key: 'edu_timetable',
    label: 'Education · Timetable & Workload',
    description: 'Courses, rooms and weekly slots with DB-level conflict detection and substitutions, plus per-faculty workload aggregation against configurable norms.',
    tabId: 'edu-timetable',
    defaultPricing: { monthly_price: 1499, yearly_price: 14999 },
  },
  {
    key: 'edu_contracts',
    label: 'Education · Faculty Contracts',
    description: 'Semester / academic-year / visiting / adjunct contracts with expiry dashboards and renewal workflow.',
    tabId: 'edu-contracts',
    defaultPricing: { monthly_price: 999, yearly_price: 9999 },
  },
  {
    key: 'edu_accreditation',
    label: 'Education · Accreditation',
    description: 'NAAC / NBA / AICTE / UGC / NIRF / ISO frameworks with weighted readiness scoring and exports.',
    tabId: 'edu-accreditation',
    defaultPricing: { monthly_price: 1999, yearly_price: 19999 },
  },
  {
    key: 'edu_research',
    label: 'Education · Research & Publications',
    description: 'Publications, patents, grants and researcher IDs (ORCID/Scopus) with annual research reports.',
    tabId: 'edu-research',
    defaultPricing: { monthly_price: 1199, yearly_price: 11999 },
  },
  {
    key: 'edu_grievance',
    label: 'Education · Grievance Management',
    description: 'Anonymous, SLA-tracked, multi-level grievance workflow (POSH, academic, ethics, whistleblower).',
    tabId: 'edu-grievance',
    defaultPricing: { monthly_price: 899, yearly_price: 8999 },
  },
  {
    key: 'edu_student_worker',
    label: 'Education · Student Workers',
    description: 'Teaching/research assistants, interns and campus ambassadors with assignments and hour caps.',
    tabId: 'edu-student-workers',
    defaultPricing: { monthly_price: 699, yearly_price: 6999 },
  },
  {
    key: 'edu_adjunct',
    label: 'Education · Adjunct Faculty',
    description: 'Course-wise adjunct appointments with hourly compensation and renewal.',
    tabId: 'edu-adjunct',
    defaultPricing: { monthly_price: 699, yearly_price: 6999 },
  },
  {
    key: 'edu_integrations',
    label: 'Education · Integrations',
    description: 'Connectors for LMS (Moodle/Canvas/Classroom), SIS, M365/Google Workspace and SSO (SAML/OAuth).',
    tabId: 'edu-integrations',
    defaultPricing: { monthly_price: 1499, yearly_price: 14999 },
  },
];

// Combined catalog used by the superadmin plan + tenant editors to render add-on
// toggles. AI-center gating helpers below intentionally stay on the AI-only list.
export const ALL_ADDON_DEFINITIONS = [...AI_DASHBOARD_ADDON_DEFINITIONS, ...SUITE_ADDON_DEFINITIONS];
export const ALL_ADDON_KEYS = ALL_ADDON_DEFINITIONS.map((addon) => addon.key);

// True when the tenant is entitled to the AI Assistant add-on — either the
// subscription plan includes it or the tenant has it enabled as an additional
// add-on (both merge into the effective usage-limit `addons`). Superadmin always
// has access. Used to gate the AI Assistant navigation + routes.
export const hasAiAssistantAddon = (user) => {
  if (!user) return false;
  if (user.isSuperadmin) return true;
  const addons = user?.limits?.addons;
  return Array.isArray(addons) && addons.includes('ai_assistant');
};

export const AI_DASHBOARD_ADDON_LOOKUP = ALL_ADDON_DEFINITIONS.reduce(
  (acc, addon) => ({
    ...acc,
    [addon.key]: addon,
  }),
  {}
);

const normalizeCurrency = (value) => String(value || 'INR').trim().toUpperCase() || 'INR';

const normalizeAmount = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const getAiDashboardAddonPricing = (limits = {}, addonKey) => {
  const addon = AI_DASHBOARD_ADDON_LOOKUP[addonKey];
  const storedPricing = limits?.addon_pricing?.[addonKey] || {};
  const defaultPricing = addon?.defaultPricing || {};

  return {
    monthly_price: normalizeAmount(storedPricing.monthly_price, normalizeAmount(defaultPricing.monthly_price, 0)),
    yearly_price: normalizeAmount(storedPricing.yearly_price, normalizeAmount(defaultPricing.yearly_price, 0)),
    currency: normalizeCurrency(storedPricing.currency || limits?.currency || limits?.pricing_controls?.currency || 'INR'),
  };
};

export const isAiDashboardAddonEnabled = (limits = {}, addonKey, isSuperadmin = false) => {
  if (isSuperadmin) return true;
  const addons = Array.isArray(limits?.addons) ? limits.addons : [];
  return addons.includes(addonKey);
};

export const listEnabledAiDashboardAddons = (limits = {}, isSuperadmin = false) =>
  AI_DASHBOARD_ADDON_DEFINITIONS.filter((addon) => isAiDashboardAddonEnabled(limits, addon.key, isSuperadmin));

export const getAiDashboardAddonTotals = (limits = {}, isSuperadmin = false) =>
  listEnabledAiDashboardAddons(limits, isSuperadmin).reduce(
    (acc, addon) => {
      const pricing = getAiDashboardAddonPricing(limits, addon.key);
      return {
        monthly_price: acc.monthly_price + Number(pricing.monthly_price || 0),
        yearly_price: acc.yearly_price + Number(pricing.yearly_price || 0),
        currency: pricing.currency || acc.currency || 'INR',
      };
    },
    { monthly_price: 0, yearly_price: 0, currency: normalizeCurrency(limits?.currency || limits?.pricing_controls?.currency || 'INR') }
  );

export const formatAddonPrice = (pricing = {}, cadence = 'monthly') => {
  const currency = normalizeCurrency(pricing.currency);
  const amount = cadence === 'yearly'
    ? normalizeAmount(pricing.yearly_price, 0)
    : normalizeAmount(pricing.monthly_price, 0);

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
};
